import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { useSettingsStore } from './stores/settings';
import { ProtectedRoute, AdminRoute, GuestRoute } from './components/auth/ProtectedRoute';
import { ToastContainer } from './components/ui/Toast';
import { LoadingScreen } from './components/ui/Spinner';
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import { CookieConsent } from './components/ui/CookieConsent';
import { BackToTop } from './components/ui/BackToTop';
import { ScrollToTop } from './components/utils/ScrollToTop';

// Lazy load pages
const LandingPage = lazy(() => import('./pages/Landing'));
const GamesPage = lazy(() => import('./pages/Games'));
const ContributePage = lazy(() => import('./pages/Contribute'));
const ShopPage = lazy(() => import('./pages/Shop'));
const LoginPage = lazy(() => import('./pages/auth/Login'));
const RegisterPage = lazy(() => import('./pages/auth/Register'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPassword'));
const OAuthCallbackPage = lazy(() => import('./pages/auth/OAuthCallback'));
const CompleteProfilePage = lazy(() => import('./pages/auth/CompleteProfile'));
const DashboardPage = lazy(() => import('./pages/dashboard/Dashboard'));
const GuildPage = lazy(() => import('./pages/dashboard/Guild'));
const GuildSecurityPage = lazy(() => import('./pages/dashboard/GuildSecurity'));
const GuildRaidPage = lazy(() => import('./pages/dashboard/GuildRaid'));
const GuildAutomodPage = lazy(() => import('./pages/dashboard/GuildAutomod'));
const GuildWelcomerPage = lazy(() => import('./pages/dashboard/GuildWelcomer'));
const GuildRolesPage = lazy(() => import('./pages/dashboard/GuildRoles'));
const GuildMessagesPage = lazy(() => import('./pages/dashboard/GuildMessages'));
const GuildWebhooksPage = lazy(() => import('./pages/dashboard/GuildWebhooks'));
const GuildBlocksPage = lazy(() => import('./pages/dashboard/GuildBlocks'));
const GuildMembersPage = lazy(() => import('./pages/dashboard/GuildMembers'));
const GuildSettingsPage = lazy(() => import('./pages/dashboard/GuildSettings'));
const GuildAnalyticsPage = lazy(() => import('./pages/dashboard/GuildAnalytics'));
const GuildPlayerCheckPage = lazy(() => import('./pages/dashboard/GuildPlayerCheck'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsers'));
const AdminGuildsPage = lazy(() => import('./pages/admin/AdminGuilds'));
const AdminActivityPage = lazy(() => import('./pages/admin/AdminActivity'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettings'));
const AdminEmailTemplatesPage = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminEmailLogsPage = lazy(() => import('./pages/admin/AdminEmailLogs'));
const AdminRolesPage = lazy(() => import('./pages/admin/AdminRoles'));
const AdminApiKeysPage = lazy(() => import('./pages/admin/AdminApiKeys'));
const AdminApplicationKeysPage = lazy(() => import('./pages/admin/AdminApplicationKeys'));
const AdminLanguagesPage = lazy(() => import('./pages/admin/AdminLanguages'));
const AdminGuildDetailPage = lazy(() => import('./pages/admin/AdminGuildDetail'));
const AdminOrdersPage = lazy(() => import('./pages/admin/AdminOrders'));
const AdminProductsPage = lazy(() => import('./pages/admin/AdminProducts'));
const AdminPurchasesPage = lazy(() => import('./pages/admin/AdminPurchases'));
const AccountSettingsPage = lazy(() => import('./pages/settings/AccountSettings'));
const NotificationsPage = lazy(() => import('./pages/settings/Notifications'));
const TwoFactorPage = lazy(() => import('./pages/settings/TwoFactor'));
const ApiKeysPage = lazy(() => import('./pages/settings/ApiKeys'));
const ActivityLogPage = lazy(() => import('./pages/settings/ActivityLog'));
const LinkedServersPage = lazy(() => import('./pages/settings/LinkedServers'));
const GameManagementPage = lazy(() => import('./pages/settings/GameManagement'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const StatusPage = lazy(() => import('./pages/Status'));
const HelpPage = lazy(() => import('./pages/Help'));
const DocsPage = lazy(() => import('./pages/docs/Docs'));
const ApiDocsPage = lazy(() => import('./pages/docs/ApiDocs'));
const MinecraftPluginDocsPage = lazy(() => import('./pages/docs/MinecraftPluginDocs'));
const MinecraftModDocsPage = lazy(() => import('./pages/docs/MinecraftModDocs'));
const ProxyDocsPage = lazy(() => import('./pages/docs/ProxyDocs'));
const RustDocsPage = lazy(() => import('./pages/docs/RustDocs'));
const CS2DocsPage = lazy(() => import('./pages/docs/CS2Docs'));
const CsSourceDocsPage = lazy(() => import('./pages/docs/CsSourceDocs'));
const GettingStartedDocsPage = lazy(() => import('./pages/docs/GettingStartedDocs'));
const InstallationDocsPage = lazy(() => import('./pages/docs/InstallationDocs'));
const DashboardDocsPage = lazy(() => import('./pages/docs/DashboardDocs'));
const SecurityDocsPage = lazy(() => import('./pages/docs/SecurityDocs'));
const RaidProtectionDocsPage = lazy(() => import('./pages/docs/RaidProtectionDocs'));
const MessagesDocsPage = lazy(() => import('./pages/docs/MessagesDocs'));
const VerifyPage = lazy(() => import('./pages/Verify'));
const ProductDetailPage = lazy(() => import('./pages/shop/ProductDetail'));
const OrdersPage = lazy(() => import('./pages/shop/Orders'));
const OrderDetailPage = lazy(() => import('./pages/shop/OrderDetail'));
const PurchasesPage = lazy(() => import('./pages/shop/Purchases'));
const ChangelogPage = lazy(() => import('./pages/Changelog'));
const PrivacyPage = lazy(() => import('./pages/legal/Privacy'));
const TermsPage = lazy(() => import('./pages/legal/Terms'));
const ShowcasePage = lazy(() => import('./pages/Showcase'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));
const UnauthorizedPage = lazy(() => import('./pages/errors/Unauthorized'));
const ServerErrorPage = lazy(() => import('./pages/errors/ServerError'));
const ServiceUnavailablePage = lazy(() => import('./pages/errors/ServiceUnavailable'));

function App() {
  const { user, sessionValidated, validateSession } = useAuthStore();
  const { settings, fetchSettings } = useSettingsStore();

  // Fetch public settings on initial load
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Validate session on initial load if we have a cached user
  useEffect(() => {
    if (user && !sessionValidated) {
      validateSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Google Analytics */}
      {settings?.ga_enabled && settings.ga_measurement_id && (
        <GoogleAnalytics
          measurementId={settings.ga_measurement_id}
          debugMode={settings.ga_debug_mode}
          anonymizeIp={settings.ga_anonymize_ip}
          trackPageViews={settings.ga_track_page_views}
          trackOutboundLinks={settings.ga_track_outbound_links}
        />
      )}

      <ScrollToTop />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/contribute" element={<ContributePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:slug" element={<ProductDetailPage />} />
          <Route
            path="/shop/orders"
            element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/orders/:orderNumber"
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/purchases"
            element={
              <ProtectedRoute>
                <PurchasesPage />
              </ProtectedRoute>
            }
          />

          {/* Public utility routes */}
          <Route path="/status" element={<StatusPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/showcase" element={<ShowcasePage />} />
          <Route path="/changelog" element={<ChangelogPage />} />

          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Documentation routes */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/api" element={<ApiDocsPage />} />
          <Route path="/docs/minecraft/plugin" element={<MinecraftPluginDocsPage />} />
          <Route path="/docs/minecraft/mod" element={<MinecraftModDocsPage />} />
          <Route path="/docs/proxy" element={<ProxyDocsPage />} />
          <Route path="/docs/getting-started" element={<GettingStartedDocsPage />} />
          <Route path="/docs/installation" element={<InstallationDocsPage />} />
          <Route path="/docs/dashboard" element={<DashboardDocsPage />} />
          <Route path="/docs/security" element={<SecurityDocsPage />} />
          <Route path="/docs/raid-protection" element={<RaidProtectionDocsPage />} />
          <Route path="/docs/messages" element={<MessagesDocsPage />} />
          <Route path="/docs/rust" element={<RustDocsPage />} />
          <Route path="/docs/cs2" element={<CS2DocsPage />} />
          <Route path="/docs/cs-source" element={<CsSourceDocsPage />} />

          {/* Verification route */}
          <Route path="/verify" element={<VerifyPage />} />

          {/* Auth routes (guest only) */}
          <Route
            path="/login"
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route
            path="/register"
            element={
              <GuestRoute>
                <RegisterPage />
              </GuestRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <GuestRoute>
                <ForgotPasswordPage />
              </GuestRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <GuestRoute>
                <ResetPasswordPage />
              </GuestRoute>
            }
          />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />

          {/* Complete profile (for Steam users without email) */}
          <Route
            path="/complete-profile"
            element={
              <ProtectedRoute>
                <CompleteProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Dashboard routes (protected) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId"
            element={
              <ProtectedRoute>
                <GuildPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/security"
            element={
              <ProtectedRoute>
                <GuildSecurityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/raid"
            element={
              <ProtectedRoute>
                <GuildRaidPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/automod"
            element={
              <ProtectedRoute>
                <GuildAutomodPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/welcomer"
            element={
              <ProtectedRoute>
                <GuildWelcomerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/roles"
            element={
              <ProtectedRoute>
                <GuildRolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/messages"
            element={
              <ProtectedRoute>
                <GuildMessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/webhooks"
            element={
              <ProtectedRoute>
                <GuildWebhooksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/blocks"
            element={
              <ProtectedRoute>
                <GuildBlocksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/members"
            element={
              <ProtectedRoute>
                <GuildMembersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/settings"
            element={
              <ProtectedRoute>
                <GuildSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/analytics"
            element={
              <ProtectedRoute>
                <GuildAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/guild/:guildId/player-check"
            element={
              <ProtectedRoute>
                <GuildPlayerCheckPage />
              </ProtectedRoute>
            }
          />

          {/* Profile route */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Settings routes */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/2fa"
            element={
              <ProtectedRoute>
                <TwoFactorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/api-keys"
            element={
              <ProtectedRoute>
                <ApiKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/activity"
            element={
              <ProtectedRoute>
                <ActivityLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/servers"
            element={
              <ProtectedRoute>
                <LinkedServersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/game-management"
            element={
              <ProtectedRoute>
                <GameManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/guilds"
            element={
              <AdminRoute>
                <AdminGuildsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/guilds/:guildId"
            element={
              <AdminRoute>
                <AdminGuildDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <AdminRoute>
                <AdminOrdersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminRoute>
                <AdminProductsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/purchases"
            element={
              <AdminRoute>
                <AdminPurchasesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <AdminRoute>
                <AdminActivityPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <AdminSettingsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email/templates"
            element={
              <AdminRoute>
                <AdminEmailTemplatesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/email/logs"
            element={
              <AdminRoute>
                <AdminEmailLogsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <AdminRoute>
                <AdminRolesPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              <AdminRoute>
                <AdminApiKeysPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/application-keys"
            element={
              <AdminRoute>
                <AdminApplicationKeysPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/languages"
            element={
              <AdminRoute>
                <AdminLanguagesPage />
              </AdminRoute>
            }
          />

          {/* Error pages */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="/503" element={<ServiceUnavailablePage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Toast notifications */}
      <ToastContainer />

      {/* Cookie consent banner */}
      <CookieConsent />

      {/* Back to top button */}
      <BackToTop />
    </>
  );
}

export default App;
